import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import classnames from 'classnames'
import Panel from '../../components/Panel'
import Badge from '../../components/Badge'
import Paginator from '../../components/Table/Paginator'
import { useAppSelector } from '../../shared/redux/hooks'
import QueryError from './QueryError'
import ConductorFieldKey from './ConductorFieldKey'
import {
  formatConductorStage,
  formatConductorTimestamp,
  mutationErrorMessage,
  paginatedListMode,
  shouldShowPaginator,
  truncateJson,
} from './conductorUtils'
import { useClampedPage } from './useClampedPage'
import {
  useApproveConductorActionMutation,
  useGetConductorOutboxDetailQuery,
  useGetConductorOutboxQuery,
  useRejectConductorActionMutation,
} from './conductorApi'

import { VerdictLine } from './conductorVerdicts'

import type { ConductorApprovalResult, ConductorOutboxAction } from './conductor.types'

const QUEUE_PAGE_SIZE = 50

const TierBadge: React.FC<{ tier: string, approvedAt: string | null }> = ({ tier, approvedAt }) => {
  if (tier === 'APPROVE' && approvedAt != null) {
    return (
      <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-400/30 dark:bg-slate-700 dark:text-slate-200">
        Awaiting execution
      </span>
    )
  }

  return (
    <span
      className={classnames(
        'inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset',
        tier === 'APPROVE'
          ? 'bg-orange-100 text-orange-800 ring-orange-600/40 dark:bg-orange-400/10 dark:text-orange-400 dark:ring-orange-400/20'
          : 'bg-blue-100 text-blue-800 ring-blue-600/20 dark:bg-blue-400/10 dark:text-blue-400 dark:ring-blue-400/50'
      )}
    >
      {tier === 'APPROVE' ? 'Needs approval' : tier}
    </span>
  )
}

const Evidence: React.FC<{ cuid: string }> = ({ cuid }) => {
  const { data, isError, refetch } = useGetConductorOutboxDetailQuery(cuid)

  if (isError) {
    return <QueryError message="Failed to load evidence." onRetry={() => void refetch()} />
  }
  if (data == null) {
    return <div className="py-3 text-sm text-slate-400 dark:text-slate-500">Loading evidence…</div>
  }

  return (
    <div className="space-y-3 py-3">
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Workflow
          </div>
          <div className="mt-1 text-sm text-slate-700 dark:text-slate-200">
            <Link to={`/conductor/instances/${data.instance.cuid}`} className="text-indigo-600 hover:underline dark:text-indigo-400">
              {data.instance.playbookKey} v{data.instance.playbookVersion}
            </Link>
            <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
              stage {formatConductorStage(data.instance.currentStage)}
            </span>
          </div>
          <div className="mt-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Parameters
          </div>
          <pre className="mt-1 overflow-x-auto rounded-md bg-slate-50 p-2 text-xs text-slate-700 dark:bg-slate-900 dark:text-slate-300">
            {truncateJson(data.action.params)}
          </pre>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Subject gate verdicts
          </div>
          {data.subjectVerdicts.length === 0 ? (
            <div className="mt-1 text-xs text-slate-400 dark:text-slate-500">No verdicts recorded.</div>
          ) : (
            <ul className="mt-1 space-y-1">
              {data.subjectVerdicts.map((verdict) => (
                <VerdictLine key={verdict.gateId} verdict={verdict} />
              ))}
            </ul>
          )}
          <div className="mt-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Audit trail
          </div>
          {data.auditTrail.length === 0 ? (
            <div className="mt-1 text-xs text-slate-400 dark:text-slate-500">No events yet.</div>
          ) : (
            <ul className="mt-1 space-y-1">
              {data.auditTrail.map((event) => (
                <li key={event.cuid} className="text-xs text-slate-600 dark:text-slate-300">
                  <span className="font-mono">{event.type}</span>
                  <span className="ml-2 text-slate-400 dark:text-slate-500">
                    {event.actorType} · {formatConductorTimestamp(event.createdAt)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

interface QueueRowProps {
  action: ConductorOutboxAction
  canApprove: boolean
  busy: boolean
  outcome?: ConductorApprovalResult['outcome']
  mutationError?: string
  onApprove: () => void
  onReject: (reason: string) => void
}

const QueueRow: React.FC<QueueRowProps> = ({
  action,
  canApprove,
  busy,
  outcome,
  mutationError,
  onApprove,
  onReject,
}) => {
  const [expanded, setExpanded] = useState(false)
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('')

  const approvable = canApprove && action.autonomyTier === 'APPROVE' && action.approvedAt == null

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex-1 text-left"
          aria-expanded={expanded}
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm font-medium text-slate-900 dark:text-slate-100">{action.tool}</span>
            <TierBadge tier={action.autonomyTier} approvedAt={action.approvedAt} />
            <Badge status={action.status} />
          </div>
          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {action.reason !== '' ? action.reason : action.actionType}
            <span className="ml-2 font-mono">{action.subjectCuid}</span>
            <span className="ml-2">proposed {formatConductorTimestamp(action.createdAt)}</span>
          </div>
        </button>
        {outcome != null ? (
          <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
            {outcome === 'NOT_ELIGIBLE' ? 'Already settled by someone else' : outcome}
          </span>
        ) : canApprove ? (
          <div className="flex items-center gap-2">
            {approvable && (
              <button
                type="button"
                disabled={busy}
                onClick={onApprove}
                className="rounded-md bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-500 disabled:opacity-50"
              >
                Approve
              </button>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={() => setRejecting((v) => !v)}
              className="rounded-md border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-950"
            >
              Reject
            </button>
          </div>
        ) : (
          <span className="text-xs text-slate-400 dark:text-slate-500">Read-only</span>
        )}
      </div>
      {mutationError != null && (
        <div className="border-t border-red-100 px-4 py-2 text-xs text-red-600 dark:border-red-900 dark:text-red-400">
          {mutationError}
        </div>
      )}
      {rejecting && outcome == null && (
        <div className="flex items-center gap-2 border-t border-slate-100 px-4 py-3 dark:border-slate-700">
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for rejection (required)"
            aria-label="Rejection reason"
            className="flex-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
          />
          <button
            type="button"
            disabled={busy || reason.trim() === ''}
            onClick={() => onReject(reason.trim())}
            className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-500 disabled:opacity-50"
          >
            Confirm reject
          </button>
        </div>
      )}
      {expanded && (
        <div className="border-t border-slate-100 px-4 dark:border-slate-700">
          <Evidence cuid={action.cuid} />
        </div>
      )}
    </div>
  )
}

export const Approvals: React.FC = () => {
  const { permissions } = useAppSelector((state) => state.app)
  const canApprove = permissions?.includes('conductor:approve') ?? false
  const [page, setPage] = useState(0)
  const [busyCuids, setBusyCuids] = useState<ReadonlySet<string>>(() => new Set())
  const [outcomes, setOutcomes] = useState<Record<string, ConductorApprovalResult['outcome']>>({})
  const [mutationErrors, setMutationErrors] = useState<Record<string, string>>({})

  const [approve] = useApproveConductorActionMutation()
  const [reject] = useRejectConductorActionMutation()

  const queueQuery = useGetConductorOutboxQuery({
    status: 'PENDING',
    autonomyTier: 'APPROVE',
    unapprovedOnly: true,
    resultsPerPage: QUEUE_PAGE_SIZE,
    page: page + 1,
  })

  const queueData = queueQuery.data
  useClampedPage(queueData?.total, queueData?.resultsPerPage, page, setPage)

  const markBusy = (cuid: string): void => {
    setBusyCuids((prev) => new Set(prev).add(cuid))
  }

  const markIdle = (cuid: string): void => {
    setBusyCuids((prev) => {
      const next = new Set(prev)
      next.delete(cuid)
      return next
    })
  }

  const handleApprove = async (cuid: string): Promise<void> => {
    markBusy(cuid)
    setMutationErrors((prev) => {
      const next = { ...prev }
      delete next[cuid]
      return next
    })
    try {
      const result = await approve(cuid).unwrap()
      setOutcomes((prev) => ({ ...prev, [cuid]: result.outcome }))
    } catch (error) {
      setMutationErrors((prev) => ({ ...prev, [cuid]: mutationErrorMessage(error) }))
    } finally {
      markIdle(cuid)
    }
  }

  const handleReject = async (cuid: string, reason: string): Promise<void> => {
    markBusy(cuid)
    setMutationErrors((prev) => {
      const next = { ...prev }
      delete next[cuid]
      return next
    })
    try {
      const result = await reject({ cuid, reason }).unwrap()
      setOutcomes((prev) => ({ ...prev, [cuid]: result.outcome }))
    } catch (error) {
      setMutationErrors((prev) => ({ ...prev, [cuid]: mutationErrorMessage(error) }))
    } finally {
      markIdle(cuid)
    }
  }

  const listMode =
    queueData == null
      ? null
      : paginatedListMode({
          total: queueData.total,
          results: queueData.results,
          resultsPerPage: queueData.resultsPerPage,
        })

  return (
    <div className="space-y-4 p-4">
      <div>
        <h2 className="m-0 text-lg font-semibold text-slate-900 dark:text-slate-100">Approvals</h2>
        <p className="m-0 text-xs text-slate-500 dark:text-slate-400">
          APPROVE-tier actions awaiting a human decision — approving makes an action claimable on the next executor
          tick; rejecting cancels it permanently
        </p>
        <ConductorFieldKey screen="approvals" />
      </div>

      <Panel header="Pending approvals">
        {queueQuery.isError ? (
          <QueryError message="Failed to load the approval queue." onRetry={() => void queueQuery.refetch()} />
        ) : queueData == null ? (
          <div className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</div>
        ) : listMode === 'empty' ? (
          <div className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">
            Nothing waiting — the queue is clear.
          </div>
        ) : (
          <>
            {listMode === 'page-pending' && (
              <div className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">Loading page…</div>
            )}
            {listMode === 'content' && (
              <div className="space-y-2">
                {queueData.results.map((action) => (
                  <QueueRow
                    key={action.cuid}
                    action={action}
                    canApprove={canApprove}
                    busy={busyCuids.has(action.cuid)}
                    outcome={outcomes[action.cuid]}
                    mutationError={mutationErrors[action.cuid]}
                    onApprove={() => void handleApprove(action.cuid)}
                    onReject={(reason) => void handleReject(action.cuid, reason)}
                  />
                ))}
              </div>
            )}
            {shouldShowPaginator(queueData.total, queueData.resultsPerPage) && (
              <Paginator
                className="-mx-4 mt-2 border-t border-slate-200 dark:border-slate-700"
                data={{
                  total: queueData.total,
                  resultsPerPage: queueData.resultsPerPage,
                  page,
                }}
                handlePaginatorClick={setPage}
              />
            )}
          </>
        )}
      </Panel>
    </div>
  )
}

export default Approvals
